// src/components/ChartTooltip.jsx
import React from "react";

export function formatTooltipLabel(value) {
  if (!value) return "—";

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toISOString().slice(0, 10);
  }

  if (typeof value === "string") {
    // Do not let Date.parse reinterpret chart categories such as "Sep 26"
    // as dates (JavaScript treats that particular label as 2001-09-26).
    const isIsoDate = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value);
    if (!isIsoDate) return value;

    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toISOString().slice(0, 10);
  }

  return String(value);
}

export default function ChartTooltip({
  active,
  payload,
  label,
  formatter,
  className = "",
  maxItems = 10,
  sortByValue = true,
  overflowItemLabel = "series",
}) {
  if (!active || !payload || payload.length === 0) return null;

  const EPS = 1e-9;

  // Remove any series where value is 0 (or effectively 0)
  const filteredPayload = payload.filter((entry) => {
    const v = Number(entry?.value) || 0;
    return Math.abs(v) > EPS;
  });

  // If all categories are zero, show nothing at all
  if (filteredPayload.length === 0) return null;

  const headerText = formatTooltipLabel(label);
  const orderedPayload = sortByValue
    ? [...filteredPayload].sort((left, right) => Math.abs(Number(right?.value) || 0) - Math.abs(Number(left?.value) || 0))
    : filteredPayload;
  const visiblePayload = maxItems ? orderedPayload.slice(0, maxItems) : orderedPayload;
  const hiddenPayload = maxItems ? orderedPayload.slice(maxItems) : [];
  const hiddenTotal = hiddenPayload.reduce((sum, entry) => sum + (Number(entry?.value) || 0), 0);
  const formattedHiddenTotal = formatter ? formatter(hiddenTotal, `Additional ${overflowItemLabel}`) : hiddenTotal;

  return (
    <div
      className={`rounded-lg border border-border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-lg ${className}`}
    >
      <div className="font-medium mb-1">{headerText}</div>

      <ul className="space-y-0.5">
        {visiblePayload.map((entry, idx) => {
          let displayValue = entry.value;
          let displayName = entry.name;

          if (formatter) {
            const formatted = formatter(
              entry.value,
              entry.name,
              entry,
              idx,
              orderedPayload
            );
            if (Array.isArray(formatted)) {
              displayValue = formatted[0];
              displayName = formatted[1] ?? entry.name;
            } else {
              displayValue = formatted;
            }
          }

          const dotColor = entry?.color || entry?.fill || "currentColor";

          return (
            <li key={idx} className="flex items-start gap-2">
              <span
                className="mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: dotColor }}
              />
              <span><span className="text-muted">{displayName}:</span>{" "}<span className="font-medium">{displayValue}</span></span>
            </li>
          );
        })}
        {hiddenPayload.length ? (
          <li className="border-t border-border pt-1 text-muted">
            {hiddenPayload.length} additional {overflowItemLabel}: {Array.isArray(formattedHiddenTotal) ? formattedHiddenTotal[0] : formattedHiddenTotal}
          </li>
        ) : null}
      </ul>
    </div>
  );
}
